import React, { useCallback, useEffect, useState } from "react";
import { Loader2, ExternalLink, X, FileDown, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import EditOrderDialog from "@/components/EditOrderDialog";
import { serviceLabel } from "@/components/booking/constants";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const ConsignmentPanel = ({ orderId, onClose, onSaved }) => {
  const [order, setOrder] = useState(null);
  const load = useCallback(() => { api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => setOrder(false)); }, [orderId]);
  useEffect(() => { load(); }, [load]);
  if (order === null) return <div className="p-4"><Loader2 className="h-5 w-5 animate-spin text-kilted-green" /></div>;
  if (!order) return <p className="p-4 text-sm text-red-600">Couldn't load this consignment.</p>;
  const addr = (a) => [a.business, a.line1, a.city, a.postcode].filter(Boolean).join(", ");
  return (
    <div className="rounded-lg border border-kilted-green/30 bg-secondary/40 p-4 text-sm" data-testid="manifest-consignment-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-heading text-base font-bold text-kilted-green">{order.tracking_number}</p>
          <p className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleDateString("en-GB")} · {serviceLabel(order.service)} · {order.distance_km} km</p>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="manifest-consignment-close"><X className="h-4 w-4" /></button>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <div><p className="text-xs font-semibold uppercase text-muted-foreground">Pickup</p><p>{addr(order.pickup)}</p><p className="text-xs text-muted-foreground">{[order.pickup.contact_name, order.pickup.contact_phone].filter(Boolean).join(" · ")}</p></div>
        <div><p className="text-xs font-semibold uppercase text-muted-foreground">Drop-off</p><p>{addr(order.dropoff)}</p><p className="text-xs text-muted-foreground">{[order.dropoff.contact_name, order.dropoff.contact_phone].filter(Boolean).join(" · ")}</p></div>
      </div>
      <p className="mt-2">{(order.items || []).map((i) => `${i.quantity}× ${i.size}${i.parcel_description ? ` (${i.parcel_description})` : ""}`).join(", ")}</p>
      {(order.dropoff.customer_reference || order.pickup.customer_reference) && <p className="text-xs text-muted-foreground">Ref: {order.dropoff.customer_reference || order.pickup.customer_reference}</p>}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <StatusBadge status={order.status} />
        <span className="font-bold">{money(order.price.total)} inc GST{order.price.manual_override ? " (manual price)" : ""}</span>
        <EditOrderDialog order={order} onSaved={() => { load(); onSaved(); }} />
        <a href={`/orders/${order.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-kilted-green underline" data-testid="manifest-open-full">
          Full details & POD <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  );
};

const ManifestShare = ({ customerId, month, defaultEmail }) => {
  const [to, setTo] = useState(defaultEmail || "");
  const [busy, setBusy] = useState("");
  useEffect(() => { setTo(defaultEmail || ""); }, [defaultEmail]);
  const download = async () => {
    setBusy("pdf");
    try {
      const r = await api.get(`/admin/eom/${customerId}/manifest.pdf`, { params: { month }, responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || `manifest-${month}.pdf`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Couldn't create the PDF"); } finally { setBusy(""); }
  };
  const email = async () => {
    setBusy("email");
    try {
      const { data } = await api.post(`/admin/eom/${customerId}/manifest/email`, { month, to: to.trim() || null });
      toast.success(`Manifest emailed to ${data.to}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-secondary/40 p-2" data-testid="manifest-share">
      <Button size="sm" variant="outline" onClick={download} disabled={!!busy} className="gap-1.5" data-testid="manifest-download-pdf">
        {busy === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} Download PDF
      </Button>
      <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@email.com" className="h-9 w-full min-w-0 sm:w-auto sm:flex-1 sm:max-w-xs" data-testid="manifest-email-input" />
      <Button size="sm" onClick={email} disabled={!!busy || !to.trim()} className="gap-1.5 bg-kilted-green hover:bg-kilted-greenLight" data-testid="manifest-email-button">
        {busy === "email" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Email PDF
      </Button>
    </div>
  );
};

export const AccountManifestDialog = ({ customer, initialMonth, onClose, onChanged }) => {
  const [month, setMonth] = useState(initialMonth);
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => {
    setData(null);
    api.get(`/admin/eom/${customer.customer_id}/manifest`, { params: { month } }).then((r) => setData(r.data)).catch(() => setData({ rows: [], totals: { jobs: 0, total: 0, unbilled: 0 } }));
  }, [customer.customer_id, month]);
  useEffect(() => { load(); }, [load]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[92vh] max-w-4xl flex-col overflow-hidden" data-testid="account-manifest-dialog">
        <DialogHeader>
          <DialogTitle>{customer.customer_name} — monthly manifest</DialogTitle>
          <DialogDescription>Every consignment for this account. Tap a consignment to view or edit it before it's invoiced.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="month" value={month === "unbilled" ? "" : month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="w-44" data-testid="manifest-month-input" />
          <Button size="sm" variant={month === "unbilled" ? "default" : "outline"} onClick={() => setMonth("unbilled")} data-testid="manifest-unbilled-button"
            className={month === "unbilled" ? "bg-kilted-green" : ""}>All unbilled</Button>
          {data && <span className="ml-auto text-sm" data-testid="manifest-totals"><b>{data.totals.jobs}</b> jobs · {money(data.totals.total)} · unbilled <b className="text-kilted-green">{money(data.totals.unbilled)}</b></span>}
        </div>
        {data && data.rows.length > 0 && <ManifestShare customerId={customer.customer_id} month={month} defaultEmail={data.customer.email} />}
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
          {openId && <ConsignmentPanel orderId={openId} onClose={() => setOpenId(null)} onSaved={() => { load(); onChanged(); }} />}
          {!data ? <Loader2 className="h-5 w-5 animate-spin text-kilted-green" /> : data.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="manifest-empty">No consignments for this period.</p>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Consignment</TableHead><TableHead>Route / ref</TableHead><TableHead>Items</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.rows.map((r) => (
                  <TableRow key={r.id} data-testid={`manifest-row-${r.tracking_number}`} onClick={() => r.kind === "courier" && setOpenId(r.id)}
                    className={`${r.kind === "courier" ? "cursor-pointer" : ""} ${openId === r.id ? "bg-kilted-gold/10" : ""} ${r.status === "cancelled" ? "opacity-50" : ""}`}>
                    <TableCell className="whitespace-nowrap text-xs">{r.date}</TableCell>
                    <TableCell className="font-semibold text-kilted-green">{r.kind === "paint" ? `Paint ${r.tracking_number}` : r.tracking_number}</TableCell>
                    <TableCell className="text-xs">{r.kind === "paint" ? `To ${r.dropoff}` : `${r.pickup} → ${r.dropoff}${r.dropoff_business ? ` · ${r.dropoff_business}` : ""}`}{r.reference && <div className="text-muted-foreground">{r.reference}</div>}</TableCell>
                    <TableCell>{r.items}{r.kind === "courier" && r.service !== "standard" ? <div className="text-xs text-muted-foreground">{serviceLabel(r.service)}</div> : null}</TableCell>
                    <TableCell>{r.kind === "paint" ? <span className="text-xs capitalize">{r.status}</span> : <StatusBadge status={r.status} />}</TableCell>
                    <TableCell className="whitespace-nowrap text-right">{money(r.total)}{r.price_override && <span className="ml-1 text-xs text-kilted-gold">*</span>}
                      <div className="text-xs text-muted-foreground">{r.billed ? (r.invoice || "invoiced") : r.on_account ? "unbilled" : "paid"}</div></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
